'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import ServiceCard from '@/app/homepage/components/ServiceCard';
import ProjectCard from '@/app/homepage/components/ProjectCard';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Navigation from '@/components/common/Navigation';
import { rfpService, RfpFormData } from '@/lib/services/rfpService';

export default function MunicipalServicesPage() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<RfpFormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectType: 'municipal',
    message: '',
    servicePage: 'municipal',
  });

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));
    
    return () => observer?.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    const result = await rfpService.submitInquiry(formData);
    if (result.success) {
      setSubmitStatus('success');
      setFormData({ name: '', company: '', email: '', phone: '', projectType: 'municipal', message: '', servicePage: 'municipal' });
    } else {
      setSubmitStatus('error');
      setErrorMessage(result.error || 'Failed to submit inquiry.');
    }
    setIsSubmitting(false);
  };

  return (
    <main className="min-h-screen bg-background">
      <Navigation />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center border-b border-border">
        <div className="absolute inset-0">
          <img
            src="/assets/images/municipal_try_1-1786132236849.jpg"
            alt="Vermeer drilling equipment and vacuum excavator on a residential street jobsite for municipal underground utility work"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <Link 
            href="/homepage" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 reveal"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back to Home
          </Link>
          
          <div className="mb-6 reveal" style={{ transitionDelay: '100ms' }}>
            <Icon name="BuildingOfficeIcon" size={64} className="text-success mx-auto mb-6" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground mb-6 reveal" style={{ transitionDelay: '200ms' }}>
            Municipal Utilities
            <br />
            <span className="text-gradient-orange">Infrastructure</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 reveal" style={{ transitionDelay: '300ms' }}>
            Horizontal directional drilling for water, sewer, and stormwater systems serving cities and municipalities. 
            Upgrading critical infrastructure with minimal disruption to residents and businesses.
          </p>
          
          <Link
            href="/contact"
            className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all reveal inline-block"
            style={{ transitionDelay: '400ms' }}
          >
            Request Municipal Project Quote
          </Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Services & Capabilities
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Municipal Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Comprehensive underground utility solutions for water, sewer, and stormwater infrastructure — delivered with precision and minimal disruption to residents and businesses.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ServiceCard 
              icon="CogIcon"
              title="Horizontal Directional Drilling"
              description="Our primary method for trenchless underground utility installation beneath roads, waterways, and existing infrastructure. Using Vermeer and Ditch Witch drilling rigs, we install water mains, sewer force mains, and stormwater conduit with bore lengths up to 1,500 feet and pipe diameters from 1/2&quot; to 16&quot;. HDD allows municipalities to upgrade critical infrastructure with zero disruption to traffic, businesses, or residents above ground."
              specs={[
                'Water main installation & replacement',
                'Sewer force main installations',
                'Road, railroad & waterway crossings',
                'Pipe diameters: 1/2" to 16"',
                'Max bore length: 1,500 feet'
              ]}
              delay={100}
            />
            
            <ServiceCard 
              icon="BeakerIcon"
              title="Hydro-Vac Excavation"
              description="Pressurized water and industrial vacuum technology for safe, precise excavation around existing utilities. Hydro-vac is our go-to method for potholing and daylighting buried infrastructure before drilling, exposing utility conflicts without risk of damage. It is ideal for tight urban environments, congested utility corridors, and any situation where conventional excavation poses a risk to existing water, sewer, gas, or electrical lines."
              specs={[
                'Utility potholing & daylighting',
                'Safe excavation near live utilities',
                'Slot trenching in confined areas',
                'Debris removal & site cleanup',
                'Supports HDD & open trench operations'
              ]}
              delay={200}
            />
            
            <ServiceCard 
              icon="WrenchIcon"
              title="Open Trench"
              description="Traditional open-cut excavation for utility installations where trenchless methods are not feasible or cost-effective. Our crews handle full-depth trenching for water service lines, sanitary sewer laterals, stormwater drainage systems, and culvert installations. We follow all OSHA shoring and safety requirements and restore surfaces — pavement, curbing, and landscaping — to pre-construction condition upon completion."
              specs={[
                'Water & sewer service line installation',
                'Stormwater drainage & culverts',
                'Sanitary sewer lateral connections',
                'OSHA-compliant shoring & safety',
                'Full surface restoration included'
              ]}
              delay={300}
            />
            
            <ServiceCard 
              icon="MapPinIcon"
              title="Utility Locating"
              description="Accurate identification and marking of existing underground utilities before any excavation or drilling begins. We use electromagnetic locating equipment and ground-penetrating radar (GPR) to map buried water, sewer, gas, electric, and telecommunications lines. Our locating services reduce the risk of utility strikes, protect public safety, and keep projects on schedule by eliminating costly surprises underground."
              specs={[
                'Electromagnetic utility locating',
                'Ground-penetrating radar (GPR)',
                'Pre-construction utility mapping',
                'Conflict identification & reporting',
                'Supports all excavation methods'
              ]}
              delay={400}
            />
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <div className="hidden">
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Project Portfolio
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Municipal Infrastructure Case Studies
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Proven expertise in water, sewer, and stormwater projects for municipalities across the East Coast.
            </p>
          </div>
          
          <div className="space-y-0">
            <ProjectCard 
              title="Municipal Water Main Replacement"
              location="Baltimore, MD"
              challenge="Replace 100-year-old cast iron water main beneath historic downtown district without disrupting businesses or damaging brick streets. Required 24/7 water service during construction and preservation of historic infrastructure."
              solution="Deployed pipe bursting technology with Vermeer D60x90 for trenchless replacement. Installed 16-inch ductile iron pipe in 500-foot sections. Night operations with temporary water service connections. Zero business disruptions and historic streets preserved."
              timeline="Completed in 28 days"
              image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800"
              imageAlt="Historic downtown Baltimore water main replacement using trenchless technology"
              delay={100}
            />
            
            <ProjectCard 
              title="Sanitary Sewer System Upgrade"
              location="Trenton, NJ"
              challenge="Install 3 miles of new sanitary sewer lines to eliminate combined sewer overflows into Delaware River. Required crossing beneath active railroad, state highway, and residential neighborhoods during EPA consent decree timeline."
              solution="Executed 45 HDD bores ranging from 200 to 1,200 feet using multiple rig sizes. Coordinated with railroad and DOT for critical crossings. Installed 12-inch to 24-inch HDPE sewer pipe. Met EPA deadline with zero environmental violations."
              timeline="Completed in 90 days"
              image="https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=800"
              imageAlt="Sanitary sewer system installation crossing railroad and highway in Trenton"
              delay={200}
            />
            
            <ProjectCard 
              title="Stormwater Management System"
              location="Annapolis, MD"
              challenge="Install stormwater drainage system to prevent flooding in low-lying historic district. Required drilling beneath colonial-era buildings, narrow streets, and Chesapeake Bay waterfront without damaging foundations or seawalls."
              solution="Precision HDD with real-time monitoring to avoid historic foundations. Installed 18-inch to 36-inch HDPE stormwater pipe with outfall to bay. Coordinated with historic preservation society. Zero structural damage to historic buildings."
              timeline="Completed in 45 days"
              image="https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800"
              imageAlt="Stormwater drainage installation in historic Annapolis waterfront district"
              delay={300}
            />
            
            <ProjectCard 
              title="Emergency Water Line Repair"
              location="Dover, DE"
              challenge="Emergency replacement of failed 20-inch water main serving 5,000 residents during winter freeze. Required rapid mobilization, 24/7 operations, and completion before water reserves depleted. Frozen ground conditions complicated drilling."
              solution="Mobilized emergency response team within 4 hours. Used heated drilling fluid and specialized tooling for frozen ground. Installed 1,200 feet of new ductile iron pipe in 72-hour continuous operation. Restored water service ahead of schedule."
              timeline="Completed in 3 days"
              image="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800"
              imageAlt="Emergency water main repair operation in winter conditions in Dover Delaware"
              delay={400}
            />
          </div>
        </div>
      </section>
      </div>

      {/* Technical Expertise */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Technical Expertise
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Municipal Utility Specializations
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '100ms' }}>
              <Icon name="BeakerIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Water Systems</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Water main installation & replacement
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Service line connections
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Water treatment plant tie-ins
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Emergency repair services
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '200ms' }}>
              <Icon name="WrenchIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Sewer Systems</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Sanitary sewer lines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Force main installations
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Pump station connections
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  CSO elimination projects
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '300ms' }}>
              <Icon name="CloudIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Stormwater Management</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Stormwater drainage systems
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Culvert installations
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Detention pond connections
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Flood mitigation projects
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 reveal">
            Ready to Upgrade Your Infrastructure?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 reveal" style={{ transitionDelay: '100ms' }}>
            Contact our municipal utility specialists for a detailed infrastructure assessment and competitive quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center reveal" style={{ transitionDelay: '200ms' }}>
            <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all">
              Get Project Quote
            </button>
            <Link href="/homepage">
              <button className="px-8 py-4 border-2 border-foreground text-foreground rounded-lg font-bold text-lg hover:bg-foreground hover:text-background transition-all">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* RFP Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 relative">
            <button
              onClick={() => { setShowForm(false); setSubmitStatus('idle'); }}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="XMarkIcon" size={24} className="text-foreground" />
            </button>
            <h2 className="text-2xl font-bold text-foreground mb-2">Request Municipal Project Quote</h2>
            <p className="text-muted-foreground mb-6">Fill out the form below and we will respond within 48 hours.</p>
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="CheckCircleIcon" size={40} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Inquiry Submitted!</h3>
                <p className="text-muted-foreground mb-4">Thank you! We will contact you within 48 hours.</p>
                <button onClick={() => { setShowForm(false); setSubmitStatus('idle'); }} className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all">Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="John Smith" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Company *</label>
                    <input type="text" name="company" required value={formData.company} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="City of Example" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="john@city.gov" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone *</label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Project Details *</label>
                  <textarea name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="Tell us about your municipal project requirements, timeline, and location..." />
                </div>
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <Icon name="ExclamationCircleIcon" size={18} className="text-destructive flex-shrink-0" />
                    <p className="text-sm text-destructive">{errorMessage}</p>
                  </div>
                )}
                <button type="submit" disabled={isSubmitting} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSubmitting ? (
                    <><svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Submitting...</>
                  ) : 'Submit RFP Inquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}